import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SectionHeader from "../common/SectionHeader";
import AgencyTab from "./AgencyTab";
import UserTab from "./UserTab";
import RoleTab from "./RoleTab";
import PermissionTab from "./PermissionTab";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import useZustandStore from "../../zustandStore/useZustandStore";
import { isAgencyScopedUser } from "../../utils/agencyScope";

const TAB_IDS = ["agencies", "users", "roles", "permissions"];

const AgencyUserRoles = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab");
    const currentUser = useZustandStore((s) => s?.currentUser);
    const { hasPermission } = usePermissions();
    const canManageAgencies = hasPermission(PERMISSION_CODES.AGENCY_MANAGE);
    const canManageUsers = hasPermission(PERMISSION_CODES.USER_MANAGE);
    const agencyScoped = isAgencyScopedUser(currentUser);
    /** Scoped users with user.manage manage people only — no Agencies tab. */
    const hideAgenciesForScopedUserManager = agencyScoped && canManageUsers;
    const allowedTabs = useMemo(() => {
        const t = [];
        if (canManageAgencies && !hideAgenciesForScopedUserManager) t.push("agencies");
        if (canManageUsers) t.push("users", "roles", "permissions");
        return t;
    }, [canManageAgencies, canManageUsers, hideAgenciesForScopedUserManager]);
    const defaultTab = allowedTabs[0] ?? "agencies";

    const [activeTab, setActiveTab] = useState(() =>
        tabFromUrl && TAB_IDS.includes(tabFromUrl) ? tabFromUrl : defaultTab,
    );

    useEffect(() => {
        if (allowedTabs.length === 0) return;
        if (!tabFromUrl) {
            setSearchParams({ tab: defaultTab }, { replace: true });
            return;
        }
        if (!allowedTabs.includes(tabFromUrl)) {
            setActiveTab(defaultTab);
            setSearchParams({ tab: defaultTab }, { replace: true });
            return;
        }
        if (tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
    }, [tabFromUrl, activeTab, setSearchParams, allowedTabs, defaultTab]);

    const sectionTitle = hideAgenciesForScopedUserManager
        ? "Users & Roles Management"
        : "Agency, User & Roles Management";

    return (
        <section className="p-4 md:p-6">
            <SectionHeader title={sectionTitle} className="mb-4" />

            <div className="bg-white rounded-xl border border-gray-200 min-h-[60vh]">
                {activeTab === "agencies" && canManageAgencies && !hideAgenciesForScopedUserManager && <AgencyTab />}
                {activeTab === "users" && canManageUsers && <UserTab />}
                {activeTab === "roles" && canManageUsers && <RoleTab />}
                {activeTab === "permissions" && canManageUsers && <PermissionTab />}
            </div>
        </section>
    );
};

export default AgencyUserRoles;
